import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FeesCalculator from "@/components/FeesCalculator";

const Docs = () => {

  useEffect(() => {
    // Small delay to ensure layout is settled
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 0);
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Hero Section */}
          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold">How KBETS Works</h1>
            <p className="text-lg text-muted-foreground">
              KBETS is an esports betting platform built on BNB Chain, powered by PancakeSwap v4's new hook architecture.
              Bet on your favorite esports matches across CS2, Dota 2, League of Legends, Valorant, and more.
            </p>
            {/*
            <div className="space-y-3 p-6 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-lg font-semibold text-primary">
                Beta Launch: Progressive Rollout Starting October 23, 2025
              </p>
              <p className="text-muted-foreground">
                KBETS is launching through a progressive beta rollout to waitlist members:
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside ml-4">
                <li><strong>October 20:</strong> First wave of beta invites sent to curated users and industry experts</li>
                <li><strong>October 23:</strong> Beta invites begin rolling out to waitlist members</li>
              </ul>
              <div className="mt-4">
                <Button 
                  size="lg"
                  onClick={() => setShowWaitlist(true)}
                  className="font-semibold"
                >
                  Register for Early Access
                </Button>
              </div>
            </div>
            */}
          </div>

          {/* Core Mechanics */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold">Core Mechanics</h2>

            {/* Market Creation */}
            <Card className="p-6 space-y-4">
              <h3 className="text-2xl font-semibold">1. Match Betting</h3>
              <p className="text-muted-foreground">
                Bet on any esports match, such as:
              </p>
              <p className="text-lg font-medium italic">
                "Will Team Liquid beat Natus Vincere in the CS2 Major Final?"
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>Browse live and upcoming esports matches across multiple games.</li>
                <li>Each match begins in the Launchpad phase, where liquidity providers (LPs) deposit funds to activate betting.</li>
                <li>Once the liquidity target is met, the match goes live for betting.</li>
                <li>When funding is complete, YES/USDT and NO/USDT betting pairs are created based on initial odds.</li>
                <li>If the liquidity goal is not reached, all participants are automatically refunded in full.</li>
                <li>If the goal is reached, betting becomes instantly available on-chain.</li>
              </ul>
              <p className="text-muted-foreground">
                KBETS uses PancakeSwap v4's AMM engine with custom hooks that handle fee routing, settlement, and liquidity management.
                This setup keeps liquidity deep and odds dynamic, without relying on a traditional betting book.
              </p>
            </Card>

            {/* Trading */}
            <Card className="p-6 space-y-4">
              <h3 className="text-2xl font-semibold">2. Placing Bets</h3>
              <p className="text-muted-foreground">
                Bettors buy "YES" or "NO" shares representing each outcome of a match.
                Odds move dynamically with supply and demand, reflecting live betting sentiment in real time.
              </p>
              <p className="text-muted-foreground">
              KBETS's betting design ensures that 1 YES share + 1 NO share will always equal $1, guaranteeing balanced odds and preventing arbitrage opportunities.
              </p>
              <p className="text-muted-foreground">
                Because KBETS is built on PancakeSwap v4:
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li><strong>Instant:</strong> bets settle on-chain in one transaction.</li>
                <li><strong>Efficient:</strong> the AMM and hook logic manage liquidity and fees automatically.</li>
                <li><strong>Live Odds:</strong> odds update in real-time based on betting activity.</li>
                <li><strong>Transparent:</strong> all bets and outcomes are verifiable on-chain.</li>
              </ul>
            </Card>

            {/* Fees & Incentives */}
            <Card className="p-6 space-y-4">
              <h3 className="text-2xl font-semibold">3. Fees and Incentives</h3>
              <p className="text-muted-foreground">
                Every bet on KBETS carries a 3% transaction fee, automatically distributed to maintain the platform.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4">Recipient</th>
                      <th className="text-left py-3 px-4">Share of Fee</th>
                      <th className="text-left py-3 px-4">Example on $1M Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4">Match Creator</td>
                      <td className="py-3 px-4">33%</td>
                      <td className="py-3 px-4">$9,900</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4">Liquidity Providers</td>
                      <td className="py-3 px-4">40%</td>
                      <td className="py-3 px-4">$12,000</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4">KBETS (Platform)</td>
                      <td className="py-3 px-4">27%</td>
                      <td className="py-3 px-4">$8,100</td>
                    </tr>
                    <tr className="font-semibold">
                      <td className="py-3 px-4">Total Fees (3%)</td>
                      <td className="py-3 px-4">—</td>
                      <td className="py-3 px-4">$30,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-muted-foreground">This model rewards everyone fairly:</p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>Match creators earn from their contributions.</li>
                <li>Liquidity providers earn yield with guaranteed principal.</li>
                <li>KBETS maintains liquidity and infrastructure.</li>
              </ul>

              <div className="mt-6">
                <h4 className="text-lg font-semibold mb-4">Fee Calculator</h4>
                <FeesCalculator />
              </div>
            </Card>

            {/* Liquidity Guarantee */}
            <Card className="p-6 space-y-4">
              <h3 className="text-2xl font-semibold">4. Liquidity Guarantee</h3>
              <p className="text-muted-foreground">
                KBETS keeps every match bettable through a protected liquidity model.
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>Liquidity providers can add or remove liquidity after each match cycle.</li>
                <li>LP capital is protected through KBETS's AMM structure and backstop fund, which are designed to prevent permanent loss of principal.</li>
                <li>During early stages, KBETS may co-provide or backstop liquidity to ensure smooth betting.</li>
                <li>PancakeSwap v4 manages swaps and settlements, while KBETS's hooks apply the betting logic.</li>
              </ul>
            </Card>

            {/* Resolution */}
            <Card className="p-6 space-y-4">
              <h3 className="text-2xl font-semibold">5. Match Resolution</h3>
              <p className="text-muted-foreground">
                Match results are verified centrally at launch but fully transparent. KBETS will publish all sources and on-chain proofs for each match outcome.
                Future updates will move toward decentralized or oracle-based resolution, with community governance and transparent dispute handling.
              </p>
            </Card>
          </div>

          {/* Market Lifecycle */}
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">Match Lifecycle Overview</h2>
            <Card className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4">Stage</th>
                      <th className="text-left py-3 px-4">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 font-semibold">1. Match Announced</td>
                      <td className="py-3 px-4">Upcoming esports matches are added to the platform.</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 font-semibold">2. Launchpad</td>
                      <td className="py-3 px-4">Liquidity providers fund the match. If the goal is met, betting opens; if not, funds are refunded.</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 font-semibold">3. Betting Live</td>
                      <td className="py-3 px-4">Users place bets on YES or NO, and odds update dynamically.</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4 font-semibold">4. Match Resolved</td>
                      <td className="py-3 px-4">KBETS verifies and settles the match outcome based on official results.</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-semibold">5. Payouts</td>
                      <td className="py-3 px-4">Winnings and fees are distributed among bettors, match creators, and LPs.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* How to Earn Points */}
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">How to Earn Points</h2>
            <Card className="p-6 space-y-4">
              <p className="text-muted-foreground">
                KBETS rewards participation through a points system that tracks user activity and early engagement.
                Points determine your airdrop allocation at the end of each season.
              </p>
              
              <div className="space-y-3">
                <h4 className="text-xl font-semibold">Season Structure</h4>
                <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                  <li>Beta Season begins October 20 for early waitlist members</li>
                  <li>Season 1 officially begins on October 24 and ends on November 5</li>
                  <li>Beta testers earn 2x points multiplier for all actions during the beta period</li>
                  <li>At the end of each season, points will be distributed based on participation and activity</li>
                  <li>Future seasons will introduce new missions, bonuses, and reward tiers</li>
                </ul>
              </div>

              <div className="space-y-3">
                <h4 className="text-xl font-semibold">Earning Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4">Action</th>
                        <th className="text-left py-3 px-4">Reward</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border">
                        <td className="py-3 px-4">Trading</td>
                        <td className="py-3 px-4">1 point per $1 traded</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-4">Launchpad Funding</td>
                        <td className="py-3 px-4">10 points per $1 provided</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-4">Market Creation</td>
                        <td className="py-3 px-4">50 points per market created</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">Referrals</td>
                        <td className="py-3 px-4">50 points per user referred + 5% of their points</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-muted-foreground">
                Every action on the platform contributes to your rank and final reward tier.
                The more you trade, create, and share, the higher your future allocation will be.
              </p>
            </Card>
          </div>

          {/* Example Walk-Through */}
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">Example Walk-Through</h2>
            <Card className="p-6 space-y-4 bg-primary/5">
              <p className="text-lg">An upcoming Dota 2 match is added:</p>
              <p className="text-xl font-medium italic">
                "Will Team Spirit beat Team Liquid in The International 2025 Finals?"
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>Liquidity providers fund the match and betting opens.</li>
                <li>The match reaches $1M in total bets placed.</li>
                <li>KBETS's 3% fee applies, creating $30,000 total fees.</li>
              </ul>
              <div className="space-y-2">
                <p className="font-semibold">Distribution:</p>
                <ul className="space-y-1 text-muted-foreground list-disc list-inside ml-4">
                  <li>$9,900 to the Match Creator</li>
                  <li>$12,000 to Liquidity Providers</li>
                  <li>$8,100 to KBETS</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-lg font-semibold text-primary">
                  The match creator earned $9,900,
                </p>
                <p className="text-lg font-semibold text-primary">
                  and LPs earned $12,000 for providing liquidity.
                </p>
              </div>
            </Card>
          </div>

          {/* Why KBETS Exists */}
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">Why KBETS Exists</h2>
            <Card className="p-6 space-y-4">
              <p className="text-muted-foreground">
                Traditional esports betting platforms are centralized, opaque, and take huge fees.
              </p>
              <p className="text-lg font-semibold">
                KBETS changes everything.
              </p>
              <p className="text-muted-foreground">
                Built on PancakeSwap v4, KBETS brings transparency and fairness to esports betting:
              </p>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>All bets are transparent and verifiable on-chain.</li>
                <li>Dynamic odds based on real betting activity.</li>
                <li>Lower fees with fairer distribution.</li>
                <li>Liquidity providers earn from every match.</li>
              </ul>
              <p className="text-lg font-semibold text-primary mt-4">
                KBETS brings decentralized betting to esports.
              </p>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Docs;
